import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Home, Plus, Warehouse, Paintbrush, Replace, Hammer, MoreHorizontal,
  ArrowLeft, ArrowRight, Check, Upload, FileText, X, Loader2, Info, Sparkles,
} from "lucide-react";
import api from "@/lib/api";
import { APPLICATION_TYPES } from "@/lib/citizen";
import InfoTooltip from "@/components/InfoTooltip";
import { toast } from "sonner";

const ICONS = { Home, Plus, Warehouse, Paintbrush, Replace, Hammer, MoreHorizontal };
const STEPS = ["Typ", "Plats", "Beskrivning", "Handlingar", "Granska"];
const SUGGESTED_DOCS = [
  { key: "situationsplan", label: "Situationsplan", info: "Visar fastigheten ovanifrån med byggnadens placering. Skala 1:400 eller 1:500." },
  { key: "planritning", label: "Planritning", info: "Visar rum, mått, dörrar och fönster sett ovanifrån. Skala 1:100." },
  { key: "fasadritning", label: "Fasadritning", info: "Visar alla sidor av huset utifrån. Skala 1:100." },
  { key: "sektionsritning", label: "Sektionsritning", info: "Genomskärning som visar höjder och takvinkel. Skala 1:100." },
];

const Stepper = ({ step }) => (
  <div className="flex items-center gap-2 mb-8">
    {STEPS.map((s, i) => {
      const done = i < step;
      const active = i === step;
      return (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold ${
              done ? "bg-emerald-600 text-white" : active ? "bg-blue-700 text-white" : "bg-zinc-100 text-zinc-500"
            }`}>
              {done ? <Check className="w-3.5 h-3.5 stroke-[2.5]" /> : i + 1}
            </div>
            <span className={`hidden sm:inline text-xs font-medium ${active ? "text-zinc-900" : "text-zinc-500"}`}>{s}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-emerald-600" : "bg-zinc-200"}`} />}
        </React.Fragment>
      );
    })}
  </div>
);

export default function ApplicationWizard() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    case_type: "",
    address: "",
    property_designation: "",
    title: "",
    description: "",
    files: [],
  });
  const [prefillBanner, setPrefillBanner] = useState(null);
  const [prefillExtras, setPrefillExtras] = useState({ checklist: [], meta: null });
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  // Pre-fill from PermitWizard (deterministic rule engine)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("permit_prefill");
      if (!raw) return;
      const p = JSON.parse(raw);
      // Only consume prefills that are reasonably fresh (< 1h)
      if (p.created_at && (Date.now() - new Date(p.created_at).getTime()) > 60 * 60 * 1000) {
        sessionStorage.removeItem("permit_prefill");
        return;
      }
      setData((d) => ({
        ...d,
        case_type: p.case_type || d.case_type,
        title: p.title || d.title,
        description: p.description || d.description,
      }));
      setPrefillBanner({ project_type: p.permit_meta?.project_type, verdict: p.permit_meta?.verdict });
      setPrefillExtras({ checklist: p.permit_checklist || [], meta: p.permit_meta || null });
      // Skip step 0 (type already chosen) – land on Plats
      setStep(1);
      sessionStorage.removeItem("permit_prefill");
      toast.success("Förifyllt från regelmotorn – kontrollera och komplettera.");
    } catch (e) {
      // bad json – just ignore
      sessionStorage.removeItem("permit_prefill");
    }
  }, []);

  const upd = (k, v) => setData({ ...data, [k]: v });
  const next = () => setStep(Math.min(step + 1, STEPS.length - 1));
  const back = () => (step === 0 ? nav("/hem") : setStep(step - 1));

  const canNext = () => {
    if (step === 0) return !!data.case_type;
    if (step === 1) return data.address.trim().length >= 4;
    if (step === 2) return data.title.trim().length >= 4 && data.description.trim().length >= 10;
    return true;
  };

  const onFilesPicked = (fileList) => {
    const arr = Array.from(fileList || []);
    const ok = arr.filter((f) => /\.(pdf|png|jpe?g)$/i.test(f.name));
    if (ok.length < arr.length) toast.warning("Endast PDF, JPG, PNG tillåts");
    // Stamp each picked file with a stable id for React keys (the File itself is not unique)
    const stamped = ok.map((f) => Object.assign(f, { __id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 6)}` }));
    upd("files", [...data.files, ...stamped]);
  };

  const removeFile = (id) => upd("files", data.files.filter((f) => f.__id !== id));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: c } = await api.post("/crm/cases", {
        title: data.title,
        description: data.description + (data.property_designation ? `\n\nFastighetsbeteckning: ${data.property_designation}` : ""),
        address: data.address,
        case_type: data.case_type,
        status: "inkommet",
        permit_checklist: prefillExtras.checklist,
        permit_meta: prefillExtras.meta,
      });
      // Upload files in sequence
      for (const f of data.files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/crm/documents", fd, {
          params: { case_id: c.case_id },
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(`Ansökan inskickad: ${c.ref}`);
      nav(`/cases/${c.case_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skicka ansökan");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up">
      <button onClick={back} data-testid="wizard-back"
        className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 mb-6">
        <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Avbryt" : "Tillbaka"}
      </button>

      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Ansök om bygglov</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
        {STEPS[step]}
      </h1>

      {prefillBanner && (
        <div className="mt-5 flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-sm px-4 py-3" data-testid="prefill-banner">
          <Sparkles className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-semibold text-emerald-900">Förifyllt från Regelmotorn (PBL/BBR)</div>
            <div className="text-emerald-800 text-xs mt-0.5">
              Bedömning <strong>{prefillBanner.verdict?.replace(/_/g, " ")}</strong>. Komplettera adress &amp; handlingar nedan.
            </div>
          </div>
        </div>
      )}

      <div className="mt-8">
        <Stepper step={step} />
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-6 lg:p-8">
        {/* STEP 0 — Typ */}
        {step === 0 && (
          <div>
            <p className="text-zinc-600 mb-6">Vad vill du bygga? Välj det som bäst beskriver ditt projekt.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {APPLICATION_TYPES.map((t) => {
                const Icon = ICONS[t.icon] || Home;
                const selected = data.case_type === t.key;
                return (
                  <button
                    key={t.key}
                    data-testid={`wizard-type-${t.key}`}
                    onClick={() => upd("case_type", t.key)}
                    className={`text-left p-5 border rounded-sm transition-all duration-200 ${
                      selected
                        ? "border-blue-700 bg-blue-50 ring-2 ring-blue-100"
                        : "border-zinc-200 bg-white hover:border-blue-400"
                    }`}
                  >
                    <Icon className={`w-6 h-6 stroke-[1.5] ${selected ? "text-blue-700" : "text-zinc-700"}`} />
                    <div className="mt-3 font-medium text-zinc-900">{t.title}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{t.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 1 — Plats */}
        {step === 1 && (
          <div className="space-y-4">
            <p className="text-zinc-600">Var ska bygget ske?</p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                Adress
              </label>
              <input
                required
                data-testid="wizard-address"
                value={data.address}
                onChange={(e) => upd("address", e.target.value)}
                placeholder="t.ex. Storgatan 12, 11455 Stockholm"
                className="mt-1 w-full px-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-base"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                Fastighetsbeteckning
                <InfoTooltip text="Fastighetens unika namn enligt lantmäteriet, t.ex. 'Vasastaden 1:43'. Finns på din fastighetsregisterbevis eller lagfartshandlingar." />
              </label>
              <input
                data-testid="wizard-designation"
                value={data.property_designation}
                onChange={(e) => upd("property_designation", e.target.value)}
                placeholder="t.ex. Vasastaden 1:43 (valfritt)"
                className="mt-1 w-full px-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-base"
              />
            </div>
          </div>
        )}

        {/* STEP 2 — Beskrivning */}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-zinc-600">Berätta kort om ditt projekt.</p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Titel</label>
              <input
                required data-testid="wizard-title"
                value={data.title}
                onChange={(e) => upd("title", e.target.value)}
                placeholder={`t.ex. "${data.case_type} av befintlig villa"`}
                className="mt-1 w-full px-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-base"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
                Beskrivning
                <InfoTooltip text="Beskriv så detaljerat du kan: storlek (kvm), höjd, material, antal våningar, syfte. Handläggaren använder detta för att bedöma planenlighet." />
              </label>
              <textarea
                required rows={6} data-testid="wizard-description"
                value={data.description}
                onChange={(e) => upd("description", e.target.value)}
                placeholder="Berätta om byggnationen – storlek, material, höjd, syfte..."
                className="mt-1 w-full px-3 py-3 border border-zinc-300 rounded-sm focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none text-base resize-none"
              />
              <div className="mt-1.5 text-xs text-zinc-500">Min 10 tecken · {data.description.length} tecken</div>
            </div>
          </div>
        )}

        {/* STEP 3 — Handlingar */}
        {step === 3 && (
          <div>
            <p className="text-zinc-600 mb-4">Bifoga ritningar och handlingar. PDF, JPG eller PNG (max 20 MB per fil).</p>
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4 text-sm text-blue-900 flex gap-3">
              <Info className="w-4 h-4 stroke-[1.5] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold mb-1">Förslag på handlingar för {data.case_type}:</div>
                <ul className="space-y-0.5 text-blue-800">
                  {SUGGESTED_DOCS.map((d) => (
                    <li key={d.key} className="flex items-center gap-2">
                      • {d.label} <InfoTooltip text={d.info} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <label
              data-testid="wizard-dropzone"
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("border-blue-500", "bg-blue-50/30"); }}
              onDragLeave={(e) => e.currentTarget.classList.remove("border-blue-500", "bg-blue-50/30")}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("border-blue-500", "bg-blue-50/30");
                onFilesPicked(e.dataTransfer.files);
              }}
              className="block border-2 border-dashed border-zinc-300 rounded-sm p-10 text-center cursor-pointer hover:border-blue-400 transition"
            >
              <Upload className="w-8 h-8 mx-auto text-zinc-400 stroke-[1.5]" />
              <div className="mt-3 text-sm font-medium text-zinc-900">Dra och släpp filer hit</div>
              <div className="text-xs text-zinc-500">eller klicka för att välja från enheten</div>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => onFilesPicked(e.target.files)}
                className="hidden"
                data-testid="wizard-file-input"
              />
            </label>

            {data.files.length > 0 && (
              <ul className="mt-4 divide-y divide-zinc-100 border border-zinc-200 rounded-sm">
                {data.files.map((f, i) => (
                  <li key={i} className="px-4 py-3 flex items-center gap-3" data-testid={`wizard-file-${i}`}>
                    <FileText className="w-4 h-4 text-blue-700 stroke-[1.5]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-900 truncate">{f.name}</div>
                      <div className="text-xs text-zinc-500">{(f.size / 1024 / 1024).toFixed(2)} MB</div>
                    </div>
                    <button onClick={() => removeFile(i)} className="p-1 hover:bg-zinc-100 rounded-sm text-zinc-500">
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {data.files.length === 0 && (
              <p className="mt-3 text-xs text-zinc-500">Du kan även bifoga handlingar senare i ärendet.</p>
            )}
          </div>
        )}

        {/* STEP 4 — Granska */}
        {step === 4 && (
          <div>
            <p className="text-zinc-600 mb-6">Kontrollera uppgifterna innan du skickar in.</p>
            <dl className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm" data-testid="wizard-review">
              <Row label="Typ" value={data.case_type} />
              <Row label="Adress" value={data.address} />
              {data.property_designation && <Row label="Fastighetsbeteckning" value={data.property_designation} />}
              <Row label="Titel" value={data.title} />
              <Row label="Beskrivning" value={data.description} multiline />
              <Row label="Handlingar" value={data.files.length === 0 ? "Inga bifogade" : `${data.files.length} fil(er): ${data.files.map((f) => f.name).join(", ")}`} multiline />
            </dl>
            <div className="mt-6 p-4 bg-zinc-50 border border-zinc-200 rounded-sm text-xs text-zinc-600">
              När du klickar <strong>Skicka in</strong> registreras din ansökan och får ett ärendenummer.
              Du kan följa status och kommunicera med din handläggare i ärendet.
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between gap-3">
        <button onClick={back} data-testid="wizard-prev"
          className="px-4 py-2.5 text-sm font-medium border border-zinc-300 rounded-sm hover:bg-zinc-100 bg-white">
          {step === 0 ? "Avbryt" : "← Föregående"}
        </button>
        {step < STEPS.length - 1 ? (
          <button
            data-testid="wizard-next"
            onClick={next}
            disabled={!canNext()}
            className="px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.98]"
          >
            Nästa <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            data-testid="wizard-submit"
            onClick={submit}
            disabled={submitting}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-sm flex items-center gap-2 disabled:opacity-60 active:scale-[0.98]"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Skicka in ansökan
          </button>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value, multiline }) => (
  <div className="px-5 py-3 flex flex-col sm:flex-row sm:items-start sm:gap-4">
    <dt className="text-xs uppercase tracking-wider font-semibold text-zinc-500 sm:w-44 flex-shrink-0">{label}</dt>
    <dd className={`mt-1 sm:mt-0 text-sm text-zinc-900 ${multiline ? "whitespace-pre-line" : ""}`}>{value || "–"}</dd>
  </div>
);
